import React, { useEffect, useState, useRef } from 'react';
import dayjs from 'dayjs';

import combinedActions from '~redux/combinedActions';
import { useAppDispatch, useAppSelector } from '~redux/hooks';

import CustomerManagementSearchAndFilterBar from './CustomerManagementSearchAndFilterBar';
import Text from '~components/atoms/Text';
import ActionButton from '~components/atoms/ActionButton';
import TabPageHeaderWrapper from '~components/atoms/TabPageHeaderWrapper';
import DownloadCSV from '~components/molecules/DownloadCSV';
import TabPageContainer from '~components/atoms/TabPageContainer';
import CustomerManagementTable from './CustomerManagementTable';
import CustomerManagementPaginator from './CustomerManagementPaginator';
import CustomerManagementCreateCustomerModal from './CustomerManagementCreateCustomerModal';
import CustomerManagementCustomerDetailsModal from './CustomerManagementCustomerDetailsModal';
import CustomerManagementDeleteCustomerModal from './CustomerManagementDeleteCustomerModal';

import {
    CustomerAccFundTypeEnum,
    CustomerAccStatusEnum,
    CustomerAccTypeEnum,
    CustomerIdTypeEnum,
    CustomerRaceEnum,
    ModuleEnum,
    PermissionsEnum,
} from '~utils/enums';
import UtilFunctions from '~utils/functions';

import * as Styles from './_customerManagement.module.scss';
import CustomerManagementCreateCollateralModal from './CustomerManagementCreateCollateralModal';
import CustomerManagementPaperGoldBalanceModal from './CustomerManagementPaperGoldBalanceModal';

const CustomerManagement = () => {
    const { Misc: { dateTimeFormat, sanitizeReferralCode } } = UtilFunctions;

    const dispatch = useAppDispatch();

    const handleCreateNewCustomerClick = () => dispatch(combinedActions.customerSetCreateNewCustomerModalIsOpen(true));
    const handleCreateCollateralClick = () => {
        dispatch(combinedActions.goldGetGoldPricesAttempt());
        dispatch(combinedActions.customerResetCustomerDetails());
        dispatch(combinedActions.customerSetCreateCollateralModalIsOpen(true));
    };
    const exportCustomerListing = () => {isReadyToExportRef.current = true; dispatch(combinedActions.customerExportCustomerListingAttempt());};
    const clearCustomerListingForExport = () => dispatch(combinedActions.customerClearCustomerListingForExport());

    const createAccessModules = useAppSelector(state => state.auth.userDetails.access).find(access => access.permission === PermissionsEnum.Create)?.modules || [];
    const allBranches = useAppSelector(state => state.branch.allBranches);
    const customerListingForExport = useAppSelector(state => state.customer.customerListingForExport);
    const getCustomerListingForExportIsLoading = useAppSelector(state => state.customer.loadings.exportCustomerListing);

    const [isExportingCustomerListing, setIsExportingCustomerListing] = useState(false);
    const isReadyToExportRef = useRef(false);
    const prevLoadingRef = useRef(false);

    useEffect(() => {
        dispatch(combinedActions.branchGetAllBranchesAttempt());
    }, []);

    const listHeader = [
        'CUSTOMER NAME',
        'ACCOUNT TYPE',
        'EMAIL',
        'CIF NO.',
        'CONTACT NO.',
        'ID TYPE',
        'ID NO.',
        'BRANCH',
        'BRANCH CODE',
        // 'GENDER',
        'NATIONALITY',
        'OCCUPATION',
        'RACE',
        // 'DATE OF BIRTH',
        'FUND TYPE',
        'PAPER GOLD BALANCE (G)',
        'EARMARK BALANCE (G)',
        'REFERRAL CODE',
        'STATUS',
        'ACCOUNT CREATED AT',
        'LAST UPDATED',
    ];

    const listData = customerListingForExport.map(customer => {
        const {
            name,
            accountType,
            email,
            CIFNo,
            idType,
            idNo,
            contactNo,
            branchName,
            branchId,
            // gender,
            nationality,
            occupation,
            race,
            // dateOfBirth,
            fundType,
            paperGoldBalance,
            earmarkBalance,
            refCode,
            status,
            createdAt,
            updatedAt,
        } = customer;

        const branchCode = allBranches.find(branch => branch.branchId === branchId)?.branchCode || '';

        return {
            name: name || '-',
            accountType: CustomerAccTypeEnum[accountType] || '-',
            email: email || '-',
            CIFNo: CIFNo || '-',
            contactNo: contactNo || '-',
            idType: CustomerIdTypeEnum[idType] || '-',
            idNo: idNo || '-',
            branchName: branchName || '-',
            branchCode: branchCode || '-',
            // gender: CustomerGenderEnum[gender] || '-',
            nationality: nationality || '-',
            occupation: occupation || '-',
            race: CustomerRaceEnum[race] || '-',
            // dateOfBirth: dayjs(dateOfBirth).format('DD MMM YYYY'),
            fundType: fundType ? CustomerAccFundTypeEnum[fundType] : '-',
            paperGoldBalance: paperGoldBalance ? `${paperGoldBalance} g` : '-',
            earmarkBalance: earmarkBalance ? `${earmarkBalance} g` : '-',
            refCode: refCode ? sanitizeReferralCode(refCode) : '-',
            status: CustomerAccStatusEnum[status] || '-',
            createdAt: createdAt ? dateTimeFormat(createdAt) : '-',
            updatedAt: updatedAt ? dateTimeFormat(updatedAt) : '-',
        };
    });

    const handleExportCustomerListingCleanup = () => {
        clearCustomerListingForExport();
        setIsExportingCustomerListing(false);
        isReadyToExportRef.current = false;
    };

    useEffect(() => {
        if (prevLoadingRef.current && !getCustomerListingForExportIsLoading && isReadyToExportRef.current) {
            if (listData.length === 0) {
                const csv = listHeader.join(',') + '\r\n';
                const blob = new Blob([csv], { type: 'text/csv' });
                const url = URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = `${dayjs().format('DD-MM-YYYY')} Customer Listing.csv`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                handleExportCustomerListingCleanup();
            }
        }

        prevLoadingRef.current = getCustomerListingForExportIsLoading;
    }, [getCustomerListingForExportIsLoading]);

    return (
        <TabPageContainer>
            <TabPageHeaderWrapper>
                <Text
                    label='Customer List'
                    className={Styles.title}
                    size='l'
                    isBold
                />

                <div className={Styles['button-container']}>
                    {createAccessModules.find(module => module === ModuleEnum.CustomerManagement)
                        && <ActionButton
                            label='Create Collateral'
                            onClick={handleCreateCollateralClick}
                        />
                    }

                    <DownloadCSV
                        buttonLabel='Export Customer Listing to CSV'
                        header={listHeader}
                        data={listData}
                        fileName={dayjs().format('DD-MM-YYYY').concat(' Customer Listing')}
                        onClick={exportCustomerListing}
                        cleanup={handleExportCustomerListingCleanup}
                        isLoading={getCustomerListingForExportIsLoading || isExportingCustomerListing}
                    />

                    {createAccessModules.find(module => module === ModuleEnum.CustomerManagement)
                        && <ActionButton
                            label='+ Create New Customer'
                            onClick={handleCreateNewCustomerClick}
                        />
                    }
                </div>
            </TabPageHeaderWrapper>

            <CustomerManagementSearchAndFilterBar />

            <CustomerManagementTable />

            <CustomerManagementPaginator />

            <>
                <CustomerManagementCreateCollateralModal />

                <CustomerManagementCreateCustomerModal />

                <CustomerManagementCustomerDetailsModal />

                <CustomerManagementDeleteCustomerModal />

                {/* <CustomerManagementPaperGoldBalanceModal /> */}
            </>
        </TabPageContainer>
    );
};

export default CustomerManagement;
